import React from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import { BarChart, PieChart } from "@mui/x-charts";
import StudentBasic from "./StudentBasic";
import { FaUserGraduate, FaCheckCircle, FaTimesCircle, FaWallet, FaPlus, FaHistory, FaStar, FaClock } from "react-icons/fa";
import "./Dashboard.css";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboardDataset"],
    queryFn: async () => {
      const req = await axiosget(`ms_dashboard/student`);
      return req?.data;
    },
  });

  console.log();
  return (
    <div className="dashboard-container">
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}

      {data && (
        <>
          {/* Main Content Grid */}
          <div className="dashboard-grid">
            {/* Left Column - Profile and Quick Stats */}
            <div className="left-column">
              <StudentBasic />
            </div>

            {/* Right Column - Main Content */}
            <div className="right-column">
              {/* Performance Metrics */}
              <div className="metrics-section">
                <StatCard value={data.regs} label="Registered Courses" icon={<FaUserGraduate />} trend="up" trendValue="5%" />
                <StatCard value={data.passed} label="Courses Passed" icon={<FaCheckCircle />} trend="up" trendValue="10%" />
                <StatCard value={data.failed} label="Courses Failed" icon={<FaTimesCircle />} trend="down" trendValue="2%" />
              </div>

              {/* Academic Overview */}
              <div className="academic-overview">
                <CGPACard />

                <AttendanceCard />
              </div>

              {/* Financial Section */}

              <FinanceCard />

              {/* Academic Chart */}
              <GradeCard />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const GradeCard = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["GradeCard"],
    queryFn: async () => {
      const req = await axiosget(`ms_course_result/grade`);
      return req?.data;
    },
  });
  return (
    <>
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}
      {data && (
        <div className="chart-card">
          <div className="chart-header">
            <h3>Grade Distribution</h3>
            <select className="semester-select">
              <option>Fall 2023</option>
              <option>Spring 2023</option>
            </select>
          </div>
          <BarChart
            series={[
              {
                data: data.count,
                color: "#6366f1",
                label: "Students Scored",
              },
            ]}
            height={300}
            xAxis={[
              {
                data: data.grades,
                scaleType: "band",
                label: "Grade",
              },
            ]}
            yAxis={[
              {
                label: "Number of Students",
              },
            ]}
            margin={{ top: 20, bottom: 50, left: 50, right: 20 }}
          />
        </div>
      )}
    </>
  );
};

// Stat Card Component
const StatCard = ({ value, label, icon, trend, trendValue }) => (
  <div className="stat-card">
    <div className="stat-icon-container">{icon}</div>
    <div className="stat-content">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  </div>
);

const CGPACard = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["CGPACard"],
    queryFn: async () => {
      const req = await axiosget(`ms_course_result/cgpa`);
      return req?.data;
    },
  });
  return (
    <>
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}
      {data && (
        <div className="cgpa-card">
          <div className="cgpa-header">
            <FaStar className="cgpa-icon" />
            <h3>Academic Performance</h3>
          </div>
          <div className="cgpa-value">{data.cgpa > 0 ? data.cgpa : "0.00"}</div>
          <div className="cgpa-label">Current CGPA</div>
          <div
            className={`cgpa-badge ${
              Number.parseFloat(data.cgpa / data.max) * 100 >= 70
                ? "excellent"
                : Number.parseFloat(data.cgpa / data.max) * 100 >= 60
                ? "good"
                : "needs-improvement"
            }`}
          >
            {Number.parseFloat(data.cgpa / data.max) * 100 >= 70
              ? "Excellent"
              : Number.parseFloat(data.cgpa / data.max) * 100 >= 60
              ? "Good"
              : "Needs Improvement"}
          </div>
          <div className="cgpa-progress">
            <div className="progress-bar" style={{ width: `${(data.cgpa / 4) * 100}%` }}></div>
          </div>
          <div className="cgpa-scale">
            <span>{Number.parseFloat(data.min).toFixed(2)}</span>
            <span>{Number.parseFloat(data.max).toFixed(2)}</span>
          </div>
        </div>
      )}
    </>
  );
};
const AttendanceCard = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["AttendanceCard"],
    queryFn: async () => {
      const req = await axiosget(`ms_course_contact/dashboard`);
      return req?.data;
    },
  });
  // Mock attendance data - replace with actual API call
  const attendanceData = [
    { label: "Present", value: data?.attended, color: "#4ade80" },
    { label: "Absent", value: data?.expected - data?.attended, color: "#f87171" },
  ];
  return (
    <>
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}
      {data && (
        <div className="attendance-card">
          <div className="cgpa-header">
            <FaClock className="cgpa-icon" />
            <h3 className="pt-3">Overall Attendance</h3>
          </div>
          <div className="attendance-chart">
            <PieChart
              series={[
                {
                  data: attendanceData,
                  innerRadius: 50,
                  outerRadius: 80,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  arcLabel: null,
                },
              ]}
              colors={["green", "red"]}
              slotProps={{
                legend: { hidden: true },
              }}
              width={200}
              height={200}
            />
            <div className="attendance-legend">
              <div className="legend-item">
                <span className="present-dot"></span>
                <span>{Number.parseFloat((data?.attended / data?.expected) * 100).toFixed(2)}% Present</span>
              </div>
              <div className="legend-item">
                <span className="absent-dot"></span>
                <span>{Number.parseFloat(((data?.expected - data?.attended) / data?.expected) * 100).toFixed(2)}% Absent</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const FinanceCard = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["myWalletDataset"],
    queryFn: async () => {
      const req = await axiosget(`ms_student_wallet_transactions`);
      return req?.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
  return (
    <>
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}
      {data && (
        <div className="financial-card">
          <div className="financial-header">
            <FaWallet className="wallet-icon" />
            <h3>Financial Overview</h3>
          </div>
          <div className="wallet-balance">
            <span className="balance-label">Wallet Balance</span>
            <span className="balance-amount">
              ₦
              {(data.wallet.amount || 0).toLocaleString("en-NG", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="financial-actions">
            <button className="action-btn primary">
              <FaPlus /> Add Funds
            </button>
            <Link to={`/app/wallet-transaction`} className="action-btn secondary">
              <FaHistory /> Transaction History
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
export default Dashboard;
